import { useEffect, useRef, useState } from 'react'
import { Eye, CirclePause, Play, SquareStop } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip } from '@/components/ui/tooltip'
import { SCORE_COLOR, getSatisfactionIcon } from '@/components/ui/feedback-bar'
import type { Feedback } from '@/types/api'
import { TOOLTIP_STYLE } from '@/pages/libraries/LibrarySidebar'
import type { CombinedColumnDef, StyleConfig } from '@/api/dowload-excel.api'
import { formatDateTime } from '@/utils/format'

interface BuildColumnsOptions {
  pageIndex: number
  pageSize: number
  surveyTypeMap: Map<string, string>
  showUnitColumn?: boolean
  canView?: boolean
  onViewDetail?: (feedback: Feedback) => void
}

const audioEventTarget = new EventTarget()

// Bảng cấu hình mã màu HEX chuẩn gửi cho Excel API cho từng mức Đánh giá
export const SATISFACTION_EXCEL_STYLES: Record<
  string,
  StyleConfig | undefined
> = {
  'Hài lòng': {
    textColor: '#6155F5',
    bold: true,
  },
  'Bình thường': {
    textColor: '#CE7A58',
    bold: true,
  },
  'Không hài lòng': {
    textColor: '#FF383C',
    bold: true,
  },
}

export function buildColumns(
  options: BuildColumnsOptions,
): CombinedColumnDef<Feedback>[] {
  const {
    pageIndex,
    pageSize,
    surveyTypeMap,
    showUnitColumn = false,
    canView = true,
    onViewDetail,
  } = options

  const getSafeText = (value: unknown, fallback = '--') => {
    if (typeof value === 'string' && value.trim()) return value
    if (typeof value === 'number') return String(value)
    return fallback
  }

  return [
    {
      id: 'stt',
      header: 'STT',
      size: 55,
      align: 'center',
      cell: ({ row }) => row.index + 1 + pageIndex * pageSize,
    },
    {
      accessorKey: 'createdAt',
      header: 'NGÀY THỰC HIỆN',
      size: 150,
      align: 'center',
      cell: ({ getValue }) => formatDateTime(getValue()),
    },
    {
      id: 'robotName',
      accessorKey: 'robotName',
      header: 'ROBOT',
      size: 150,
      align: 'left',
      cell: ({ row }) => getSafeText((row.original as any).robotName),
    },
    ...(showUnitColumn
      ? [
          {
            id: 'unitName',
            accessorKey: 'unitName',
            header: 'ĐƠN VỊ',
            size: 150,
            align: 'left',
            cell: ({ row }) => getSafeText((row.original as any).unitName),
          } satisfies CombinedColumnDef<Feedback>,
        ]
      : []),
    {
      id: 'phoneNumber',
      accessorKey: 'phoneNumber',
      header: 'SỐ ĐIỆN THOẠI',
      size: 110,
      align: 'left',
      cell: ({ row }) => getSafeText((row.original as any).phoneNumber, ''),
    },
    {
      id: 'receptionNumber',
      accessorKey: 'receptionNumber',
      header: 'SỐ THỨ TỰ XẾP HÀNG',
      size: 140,
      align: 'center',
      cell: ({ row }) => getSafeText((row.original as any).receptionNumber, ''),
    },
    {
      id: 'deskNumber',
      accessorKey: 'deskNumber',
      header: 'SỐ QUẦY',
      size: 55,
      align: 'center',
      cell: ({ row }) => getSafeText((row.original as any).deskNumber, ''),
    },
    {
      accessorKey: 'satisfactionScore',
      header: 'ĐÁNH GIÁ',
      size: 140,
      align: 'left',
      exportStatusStyles: SATISFACTION_EXCEL_STYLES,
      cell: ({ row, getValue }) => {
        const score = getValue() as number
        const label = (row.original as any).satisfactionLevel ?? String(score)
        return (
          <div
            className={`flex items-center gap-1.5 text-xs ${SCORE_COLOR[score] ?? 'text-gray-700'}`}
          >
            {getSatisfactionIcon(score)}
            <span>{label}</span>
          </div>
        )
      },
    },
    {
      id: 'feedbackType',
      header: 'LOẠI PHẢN ÁNH',
      size: 100,
      align: 'left',
      cell: ({ row }) => {
        const source = row.original as any
        const raw = source.surveyTypeName ?? source.problemNote
        return getSafeText(surveyTypeMap.get(raw) ?? raw, '')
      },
    },
    {
      id: 'actions',
      size: 110,
      align: 'center',
      exportable: false,
      cell: ({ row }) => (
        <div className="flex justify-center items-center gap-1">
          <AudioActionIcons
            href={(row.original as any).audioRecordInfo?.fileUrl}
            score={(row.original as any).satisfactionScore}
            startLabel="Phát"
            stopLabel="Dừng"
          />
          {canView && (
            <Tooltip
              title="Xem chi tiết"
              color="#fff"
              placement="left"
              styles={{ container: TOOLTIP_STYLE }}
            >
              <Button
                variant="ghost"
                size="icon"
                className="hover:!text-[#CE7A58] hover:!bg-transparent h-8 w-8"
                onClick={() => onViewDetail?.(row.original)}
              >
                <Eye size={18} />
              </Button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ]
}

function AudioActionIcons({
  href,
  score,
  startLabel,
  stopLabel,
}: {
  href?: string
  score?: number
  startLabel: string
  stopLabel: string
}) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [hasPlayed, setHasPlayed] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const hasProgress = hasPlayed || isPlaying
  const canStop = hasProgress
  const enabled = !!href && score === 2

  const formatDuration = (value?: number) => {
    if (!value || Number.isNaN(value) || !Number.isFinite(value)) return '00:00'
    const rounded = Math.max(0, Math.floor(value))
    const minute = String(Math.floor(rounded / 60)).padStart(2, '0')
    const second = String(rounded % 60).padStart(2, '0')
    return `${minute}:${second}`
  }

  const playbackTooltip = hasProgress
    ? `${formatDuration(currentTime)}/${formatDuration(duration)}`
    : startLabel

  useEffect(() => {
    if (!href) return

    const audio = new Audio(href)
    audioRef.current = audio
    audio.preload = 'metadata'

    const onLoadedMetadata = () => setDuration(audio.duration || 0)
    const onTimeUpdate = () => setCurrentTime(audio.currentTime || 0)
    const onPlay = () => setIsPlaying(true)
    const onPause = () => setIsPlaying(false)
    const onEnded = () => {
      setIsPlaying(false)
      setCurrentTime(0)
      setHasPlayed(false)
    }

    const handleGlobalPlay = (event: Event) => {
      const customEvent = event as CustomEvent
      if (customEvent.detail !== audio) {
        audio.pause()
        audio.currentTime = 0
        setCurrentTime(0)
        setIsPlaying(false)
        setHasPlayed(false)
      }
    }

    audio.addEventListener('loadedmetadata', onLoadedMetadata)
    audio.addEventListener('timeupdate', onTimeUpdate)
    audio.addEventListener('play', onPlay)
    audio.addEventListener('pause', onPause)
    audio.addEventListener('ended', onEnded)
    audioEventTarget.addEventListener('play', handleGlobalPlay)

    return () => {
      audio.pause()
      audio.removeEventListener('loadedmetadata', onLoadedMetadata)
      audio.removeEventListener('timeupdate', onTimeUpdate)
      audio.removeEventListener('play', onPlay)
      audio.removeEventListener('pause', onPause)
      audio.removeEventListener('ended', onEnded)
      audioEventTarget.removeEventListener('play', handleGlobalPlay)
    }
  }, [href])

  const handleToggleAudio = async () => {
    const audio = audioRef.current
    if (!audio) return

    if (audio.paused) {
      audioEventTarget.dispatchEvent(new CustomEvent('play', { detail: audio }))
      setHasPlayed(true)
      await audio.play().catch(() => setIsPlaying(false))
    } else {
      audio.pause()
    }
  }

  const handleStopAudio = () => {
    const audio = audioRef.current
    if (!audio) return
    audio.pause()
    audio.currentTime = 0
    setCurrentTime(0)
    setIsPlaying(false)
    setHasPlayed(false)
  }

  if (!enabled) {
    return (
      <>
        <Button variant="ghost" size="icon" className="h-8 w-8" disabled>
          <Play size={18} />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" disabled>
          <SquareStop size={18} />
        </Button>
      </>
    )
  }

  return (
    <>
      <Tooltip
        title={playbackTooltip}
        color="#fff"
        placement="left"
        styles={{ container: TOOLTIP_STYLE }}
      >
        <Button
          variant="ghost"
          size="icon"
          className="hover:!text-[#CE7A58] hover:!bg-transparent h-8 w-8"
          onClick={handleToggleAudio}
        >
          {isPlaying ? <CirclePause size={18} /> : <Play size={18} />}
        </Button>
      </Tooltip>

      <Tooltip
        title={stopLabel}
        color="#fff"
        placement="left"
        styles={{ container: TOOLTIP_STYLE }}
      >
        <span>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleStopAudio}
            disabled={!canStop}
            className={`h-8 w-8 ${canStop ? 'hover:!text-[#CE7A58] hover:!bg-transparent' : 'hover:!bg-transparent'}`}
          >
            <SquareStop size={18} />
          </Button>
        </span>
      </Tooltip>
    </>
  )
}
